/**
 * Classic Outlook for Windows composes with Word, which rewrites any pasted
 * HTML into its own model: every cell becomes a `p.MsoNormal`, images become
 * inline characters of that paragraph, and CSS it doesn't know (display:block,
 * font-size:0, rgba colors…) is dropped. See docs/outlook-signature.md.
 *
 * The signature is a ONE-ROW table of fixed-width cells, each holding a single
 * paragraph with a single image, all as tall as the logo:
 * 1. the logo (with the gap before the texts as white space), linked to the
 *    website;
 * 2. the text column (name, role, address, then MAIL, TEL and WEB on one
 *    line) cut vertically into slices, one per link: each slice holds its share
 *    of the texts and its label, and links to the email, phone or website.
 * No line breaks: once Outlook has sent the email, Gmail makes every line
 * taller than its images (Word turns `line-height:1pt` into
 * `mso-line-height-alt` in cells), and the cell background shows between
 * lines. That background can only be a colour (Outlook drops background
 * images), which dark modes invert: with white images, the gaps came out as
 * dark stripes in the Gmail app.
 * Why the table: Gmail drops image sizes when forwarding (natural file size +
 * `max-width:100%`) but keeps cell widths, so the cells hold 2x images at their
 * display size. Why ONE row: Word never writes a real top margin on paragraphs
 * (only `mso-margin-top-alt`), and the Gmail app gives each paragraph a default
 * ~1em margin: with a single row it only lands above the signature, never
 * between its lines.
 *
 * Other rules, all verified on emails sent by Outlook (on the Walker•Drawas
 * signature, which this one is built on):
 * - `font-size:1pt` + `line-height:1pt` ("at least" in Word, see paragraph());
 * - every size in pt as styles, plus px `width`/`height` attributes (image());
 * - images rendered at 2x (IMAGE_SCALE) for sharp text on retina screens,
 *   held at their display size by px width/height attributes (see image());
 * - every image as wide as its cell, so a forwarded email keeps them all at
 *   display size.
 */

const PX_TO_PT = 0.75

function pt(px: number) {
  return `${+(px * PX_TO_PT).toFixed(2)}pt`
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

interface OutlookImage {
  src: string
  width: number | undefined
  height: number
  alt: string
  href?: string
}

/**
 * Display size twice: as px `width`/`height` attributes, which Word keeps as the
 * image size (with pt styles alone it fell back to the file's natural size, so
 * 2x images showed at double size), and as pt styles for everything else.
 */
function image({ src, width, height, alt, href }: OutlookImage) {
  const size = `${width ? `width="${Math.round(width)}" ` : ''}height="${Math.round(height)}"`
  const style = `${width ? `width:${pt(width)};` : ''}height:${pt(height)};border:0;`
  const img = `<img border="0" ${size} src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" style="${style}">`
  return href
    ? `<a href="${escapeHtml(href)}" style="text-decoration:none;">${img}</a>`
    : img
}

// .05pt (1 twip, Word's smallest step) rather than 0, so Word writes the bottom
// margin inline instead of relying on the <head> style some clients drop. The
// top one still comes out as `mso-margin-top-alt` (ignored by browsers), which
// is why each cell is a single paragraph in a single row.
const PARAGRAPH_MARGIN = 'margin-top:.05pt;margin-right:0;margin-bottom:.05pt;margin-left:0;'

/**
 * `line-height:1pt` with no `mso-line-height-rule`, which Word reads as "at
 * least 1pt": each line still grows to fit its images, so nothing is cropped.
 * Browsers size lines from the paragraph's own font (Outlook's 11pt
 * `p.MsoNormal`, scaled up further by the Gmail app; Word moves our font-size
 * onto an inner span) and centre it in the line height: with a 1pt line there's
 * no room left for descender space, so each line is exactly as tall as its images.
 * An *exact* line height as tall as the image is worse for big images: browsers
 * put half the leftover height under the baseline.
 */
function paragraph(content: string) {
  return `<p class="MsoNormal" style="${PARAGRAPH_MARGIN}font-size:1.0pt;line-height:1.0pt;font-family:Arial,sans-serif;">${content}</p>`
}

/**
 * Fixed-width cell: the px `width` attribute is what Gmail keeps when
 * forwarding, and what caps the 2x images (`max-width:100%`) at display size.
 * White like the images (SIGNATURE_BACKGROUND), for the few px Gmail adds
 * under each line and the margin the Gmail app adds above the paragraph; dark
 * modes that invert colours turn it dark.
 */
function cell(content: string, width: number | undefined) {
  const size = width ? `width="${Math.round(width)}" ` : ''
  const style = `${width ? `width:${pt(width)};` : ''}background:${SIGNATURE_BACKGROUND};padding:0;border:none;`
  return `<td ${size}valign="top" bgcolor="${SIGNATURE_BACKGROUND}" style="${style}">${paragraph(content)}</td>`
}

function table(cells: string) {
  return '<table class="MsoNormalTable" border="0" cellspacing="0" cellpadding="0" style="border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;mso-padding-alt:0pt 0pt 0pt 0pt;">'
    + `<tbody><tr>${cells}</tr></tbody></table>`
}

/**
 * Text character so iOS Mail doesn't drop an image-only signature on paste.
 * Always added: it's zero-width, outside every link and unstyled (no colour,
 * border or size of its own), so it's invisible in every client, Outlook/Word
 * included. At the very end of the last cell's paragraph: the images fill
 * their cells, so any visible character would wrap onto a new line.
 */
const HIDDEN_CHAR = '&#8203;'

/**
 * Pixel density of the signature images. 2 = sharp on retina screens; the
 * display size comes from the px attributes (image()) and, when Gmail drops
 * them on forward, from the cell widths (cell()). 1 = safe everywhere but
 * grainy on retina. See docs/outlook-signature.md.
 */
const IMAGE_SCALE = 2

interface PartSize { width: number, height: number }

/**
 * The logo cell's animated GIF, a static file per colour
 * (public/signature/logo-<color>.gif, exported by the design studio) at a
 * fixed size: the 95×80 monogram plus the 24px gap before the texts, with the
 * monogram centred. The text slices are as tall (SLICE_HEIGHT in
 * server/utils/signatureImage.ts), texts centred.
 * Files at 2x: 238×206, monogram 190×160 at x 0, y 23.
 * Replacing the files: bump SIGNATURE_IMAGE_VERSION (it's in the URL).
 */
const LOGO_GIF_SIZE: PartSize = { width: 119, height: 103 }

function signatureQuery(values: SignatureValues, part?: SignatureSlice) {
  const { fullname, role, email, phone, color } = values
  return new URLSearchParams({
    fullname,
    role,
    email,
    phone,
    color,
    ...(part ? { part } : {}),
  }).toString()
}

export function useOutlookSignatureHtml(values: MaybeRefOrGetter<SignatureValues>) {
  const origin = useRequestURL().origin

  const { data: sizes } = useAsyncData(
    () => `signature-outlook:${signatureQuery(toValue(values))}`,
    async () => {
      const entries = await Promise.all(signatureSlices(toValue(values).phone).map(async slice =>
        [slice, await $fetch<PartSize>(`${origin}/api/signature-image-meta?${signatureQuery(toValue(values), slice)}&v=${SIGNATURE_IMAGE_VERSION}`)] as const,
      ))
      return Object.fromEntries(entries) as Partial<Record<SignatureSlice, PartSize>>
    },
    { watch: [() => signatureQuery(toValue(values))] },
  )

  return computed(() => {
    const current = toValue(values)
    const phoneNumber = signaturePhone(current.phone)
    const email = current.email || CLINIC.email
    const details = [current.fullname, current.role, `${CLINIC.addressLine1}, ${CLINIC.addressLine2}`].filter(Boolean).join(' – ')

    const links: Record<SignatureSlice, { alt: string, href: string }> = {
      // The first slice also carries the texts above the labels.
      mail: { alt: `${details} – ${email}`, href: `mailto:${email}` },
      tel: { alt: phoneNumber?.formatInternational() ?? '', href: `tel:${phoneNumber?.format('E.164')}` },
      web: { alt: `www.${CLINIC.website}`, href: CLINIC.websiteUrl },
    }

    const logo = cell(image({
      src: `${origin}/signature/logo-${current.color}.gif?v=${SIGNATURE_IMAGE_VERSION}`,
      ...LOGO_GIF_SIZE,
      alt: 'No. J',
      href: CLINIC.websiteUrl,
    }), LOGO_GIF_SIZE.width)

    const slices = signatureSlices(current.phone)
    return table(logo + slices.map((slice, i) => cell(image({
      src: `${origin}/api/signature-image?${signatureQuery(current, slice)}&scale=${IMAGE_SCALE}&v=${SIGNATURE_IMAGE_VERSION}`,
      width: sizes.value?.[slice]?.width,
      height: LOGO_GIF_SIZE.height,
      ...links[slice],
    }) + (i === slices.length - 1 ? HIDDEN_CHAR : ''), sizes.value?.[slice]?.width)).join(''))
  })
}

/**
 * Puts `html` on the clipboard verbatim. Copying a DOM selection (or the
 * async Clipboard API, which sanitizes) would let the browser rewrite it
 * with computed styles before Word ever sees it.
 */
export function copyRawHtml(html: string, plainText: string) {
  const onCopy = (event: ClipboardEvent) => {
    event.preventDefault()
    event.clipboardData?.setData('text/html', html)
    event.clipboardData?.setData('text/plain', plainText)
  }
  document.addEventListener('copy', onCopy, { once: true })
  try {
    return document.execCommand('copy')
  }
  finally {
    document.removeEventListener('copy', onCopy)
  }
}

/** Display size (1x) of the whole-signature image (/api/signature-image, part 'full'). */
export function useSignatureImageSize(values: MaybeRefOrGetter<SignatureValues>) {
  const origin = useRequestURL().origin
  const query = computed(() => signatureQuery(toValue(values)))

  const { data } = useAsyncData(
    () => `signature-image-meta:${query.value}`,
    () => $fetch<PartSize>(`${origin}/api/signature-image-meta?${query.value}&v=${SIGNATURE_IMAGE_VERSION}`),
    { watch: [query] },
  )

  return data
}

/**
 * The whole signature as one animated GIF: the logo GIF as is, with the texts
 * beside it, always at the logo GIF's 2x (server/utils/signatureGif.ts).
 */
export function signatureImageUrl(origin: string, values: SignatureValues) {
  return `${origin}/api/signature-image?${signatureQuery(values)}&format=gif&v=${SIGNATURE_IMAGE_VERSION}`
}

/**
 * The "Image (legacy)" signature (one animated GIF of the whole signature) in the same
 * Outlook-safe markup as useOutlookSignatureHtml(): a one-cell table exactly as
 * wide as the image, holding it at 2x with px size attributes. When Gmail
 * forwards the email it drops the image size, but keeps the cell width and adds
 * `max-width:100%`, so the image stays at its display size.
 * Ends with the character for iOS (HIDDEN_CHAR).
 */
export function useOutlookImageSignatureHtml(values: MaybeRefOrGetter<SignatureValues>) {
  const origin = useRequestURL().origin
  const size = useSignatureImageSize(values)

  return computed(() => {
    const current = toValue(values)
    const content = image({
      src: signatureImageUrl(origin, current),
      width: size.value?.width,
      height: size.value?.height ?? 0,
      alt: ['No. J', current.fullname, current.role].filter(Boolean).join(' – '),
      href: CLINIC.websiteUrl,
    }) + HIDDEN_CHAR

    return table(cell(content, size.value?.width))
  })
}
